import React, { useEffect, useRef, useState } from "react";
import Icon from "../../../Elements/Icon/Icon.tsx";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import { useCustomController } from "../../../Reusable/Form/useCustomController.js";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { getEmailListFromString } from "../../../../helpers/getEmailLstFromString.js";
import { AutoCompleteContacts } from "./AutoCompleteContacts.js";
import { UPLOADER_SIDEBAR_MOUNT_DELAY } from "../../../../utils/globalConsts.ts";
import { isEqual } from "lodash";

import "./multipleShareInput.scss";

const ADDITIONAL_DELAY = 10;

const keyCodeList = [188, 32, 9, 186, 13];
const mailReg =
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const MultipleShareInput = (props) => {
  const { getTextByKey } = useContent();
  const { fieldControls, hasError } = useCustomController(props);
  const { isSubscribed, isPro, isLoggedIn } = useUser();
  const [innerError, setInnerError] = useState();
  const [mailValue, setMailValue] = useState("");
  const [mailsList, setMailsList] = useState([]);
  const [openAutoComplete, setOpenAutoComplete] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [contactList, setContactList] = useState([]);
  const [editedMail, setEditedMail] = useState(false);

  // --------- section to submit recipient email within homepage, only in case externalProps is passed
  const setBody = props?.externalProps?.setBody;
  const submitValue = async () => {
    if (setBody) {
      setBody((prev) => ({
        ...prev,
        SendToEmail: mailsList,
      }));
    }
  };

  const bodyRecipients = props?.externalProps?.body?.SendToEmail;

  useEffect(() => {
    if (
      !isEqual(bodyRecipients, fieldControls.value) &&
      !isEqual(bodyRecipients, mailsList)
    ) {
      setMailsList(bodyRecipients || []);
    }
  }, [bodyRecipients]);
  // --------- end of section

  // handle request to get user contacts list
  const { getUserData } = useUser();

  const handleRequest = async () => {
    const res = await apiRequest(
      "ContactsLists",
      `GetUserContactsListByEmail?contactListEmail=${getUserData().UserName}`
    );
    if (res.status === "success") {
      if (res.data) {
        const favoriteFirst = res.data.ContactsList.sort((a, b) => {
          if (a.contact_list_favorite && !b.contact_list_favorite) {
            return -1;
          }
          if (!a.contact_list_favorite && b.contact_list_favorite) {
            return 1;
          }
          return 0;
        });
        setContactList(favoriteFirst);
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn()) {
      handleRequest();
    }
  }, [isLoggedIn()]);

  const subsLimit = isPro() ? 50 : 100;
  const limitNumberShare = isSubscribed()
    ? subsLimit - props.recipientsList?.length
    : 10;

  useEffect(() => {
    fieldControls.onChange(mailsList);
    submitValue();
    setInnerError(false);
  }, [mailsList]);

  useState(() => {
    if (props.defaultValue) {
      if (Array.isArray(props.defaultValue)) {
        props.defaultValue.map((item) => {
          if (mailReg.test(item)) {
            setMailsList((prev) => {
              return [...prev, item];
            });
          }
        });
      }
    } else if (mailReg.test(props.defaultValue)) {
      setMailsList([props.defaultValue]);
    }
  }, []);

  // way to fix an issue with recapcha, on recapcha its doing clean of all custom controllers values
  useEffect(() => {
    if (
      !Array.isArray(fieldControls.value) ||
      mailsList?.join("") !== fieldControls.value?.join("")
    ) {
      fieldControls.onChange(mailsList);
    }
  }, [fieldControls.value]);

  const ref = useRef(null);

  useEffect(() => {
    // Autofocus if auto focusable, field is empty and the entrance animation finished

    if (ref?.current && props?.autofocus) {
      setTimeout(() => {
        if (mailsList.length === 0) {
          ref.current?.focus();
        }

        // I decided to add 10 ms (ADDITIONAL_DELAY) for this timer
        // in order to avoid of passing sender list's data to SubjectInput component.

        // We set focus on SubjectInput at first (when 300 ms passed).
        // And then, if "mailsList.length === 0", we set focus on THIS field (when 310 ms passed).

        // If "mailsList.length !== 0" — nothing happens and focus will remain on SubjectInput.
      }, UPLOADER_SIDEBAR_MOUNT_DELAY + ADDITIONAL_DELAY);
    }
  }, [ref?.current]);

  const handleRemoveMail = (mail) => {
    setMailsList((prevMails) => prevMails.filter((item) => item !== mail));
  };

  const handleChange = (e) => {
    setEditedMail(false);
    setMailValue(e.target.value);
  };

  const handleScrollToBottomList = () => {
    const listContainer = document.querySelector(".added-mails-container");
    listContainer.scrollTop = listContainer.scrollHeight;
  };

  const handleAddToList = (value) => {
    if (mailReg.test(value)) {
      if (!mailsList.includes(value)) {
        if (mailsList.length >= limitNumberShare) {
          setInnerError(getTextByKey("limit-multiple-share-text"));
          return;
        }
        setMailsList((prev) => [...prev, value]);
        setMailValue("");
        setOpenAutoComplete(false);
        setTimeout(() => {
          handleScrollToBottomList();
        });
      } else {
        setInnerError(getTextByKey("this-email-exist-text"));
      }
    } else {
      setInnerError(getTextByKey("invalid-email-text"));
    }
  };

  const handleAddMail = (e) => {
    if (mailValue.length !== 0) {
      if (keyCodeList.includes(e.keyCode)) {
        e.preventDefault();
      }
      if (innerError) {
        setInnerError(false);
      }
      if (keyCodeList.includes(e.keyCode)) {
        if (mailReg.test(mailValue)) {
          if (!mailsList.includes(mailValue)) {
            if (mailsList.length >= limitNumberShare) {
              setInnerError(getTextByKey("limit-multiple-share-text"));
              return;
            }
            setMailsList((prev) => [...prev, mailValue]);
            setMailValue("");
            setTimeout(() => {
              handleScrollToBottomList();
            });
          } else {
            setInnerError(getTextByKey("this-email-exist-text"));
          }
        } else {
          setInnerError(getTextByKey("invalid-email-text"));
        }
      }
    }
  };

  const wasPaste = useRef(null);

  const pasteHandler = (e) => {
    wasPaste.current = true;

    const paste =
      mailValue + (e.clipboardData || window.clipboardData).getData("text");

    const { validEmailList } = getEmailListFromString(paste);

    validEmailList.forEach((mail, i) => {
      if (mailsList.includes(mail)) {
        setInnerError(getTextByKey("this-email-exist-text"));
        return;
      }
      if (i + 1 <= limitNumberShare - mailsList.length) {
        setMailsList((prev) => [...prev, mail]);
      }
    });

    setTimeout(() => {
      setMailValue("");
      handleScrollToBottomList();
    });

    setTimeout(() => {
      wasPaste.current = false;
    }, 1100);
  };

  const wasTypedTime = useRef(null);

  const debouncedAddToList = (value) => {
    setTimeout(() => {
      if (Date.now() - wasTypedTime.current > 1000 && !wasPaste.current) {
        handleAddToList(value);
      }
    }, 1000);
  };

  useEffect(() => {
    wasTypedTime.current = Date.now();
    if (mailReg.test(mailValue) && !editedMail) {
      debouncedAddToList(mailValue);
    }
  }, [mailValue]);

  useOnClickOutside(ref, () => {
    setInnerError(false);
    setOpenAutoComplete(false);
  });

  const throttleSetFocus = useRef(false);
  const handleFocus = (bool) => {
    if (!bool) {
      setTimeout(() => {
        if (!throttleSetFocus.current) {
          setIsFocus(bool);
          if (mailValue.length > 0) {
            handleAddToList(mailValue);
          }
        }
      }, 300);
    } else {
      throttleSetFocus.current = true;
      setTimeout(() => {
        throttleSetFocus.current = false;
      }, 300);
      setIsFocus(bool);
    }
  };

  const handleEditMail = (mail) => {
    setEditedMail(true);
    handleRemoveMail(mail);
    setMailValue(mail);
  };

  const placeholder = isFocus
    ? ""
    : mailsList.length === 0
    ? ""
    : mailsList[0] + (mailsList.length > 1 ? ` +${mailsList.length - 1}` : "");

  const moveableLabel = props.item.label === "moveable" ? true : false;
  return (
    <label
      className={`border-bottom-only${hasError ? " invalid-input" : ""}`}
      ref={ref}
      onFocus={() => {
        handleFocus(true);
      }}
      onBlur={() => {
        handleFocus(false);
      }}
    >
      <div className={`input-label${moveableLabel ? " moveable-fixed" : ""}`}>
        {getTextByKey("send-to-title")}
        {mailsList.length > 0 ? (
          <div className="share-mail-counter">
            {mailsList.length}
            <Icon name="user" gray />
          </div>
        ) : null}
      </div>
      <div className="mails-input-container">
        <div className={`added-mails-container${isFocus ? "" : " collapsed"}`}>
          {mailsList.map((mail) => {
            const isWithName = contactList.find(
              (contact) =>
                contact.contact_list_email === mail &&
                contact.contact_list_full_name
            );

            return isWithName ? (
              <div
                className="mail-item"
                key={mail}
                onClick={() => handleEditMail(mail)}
              >
                <Tooltip tooltipText={isWithName.contact_list_full_name}>
                  <span>{mail}</span>
                  <Icon
                    name="x"
                    gray
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveMail(mail);
                    }}
                  />
                </Tooltip>
              </div>
            ) : (
              <div
                className="mail-item"
                key={mail}
                onClick={() => handleEditMail(mail)}
              >
                <span>{mail}</span>
                <Icon
                  name="x"
                  gray
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveMail(mail);
                  }}
                />
              </div>
            );
          })}
        </div>
        {innerError ? (
          <div className="inner-error-holder">{innerError}</div>
        ) : null}
        <input
          className="input-field"
          type="email"
          ref={fieldControls.ref}
          value={mailValue}
          onChange={handleChange}
          onPaste={pasteHandler}
          onKeyDown={handleAddMail}
          onBlur={(e) => handleAddMail({ blurEvent: true, target: e.target })}
          autoComplete="ShareToEmailRecipientTitle"
          placeholder={placeholder}
        />
        {isLoggedIn() ? (
          <button
            type="button"
            className={`contact-list-icon-holder`}
            onClick={(e) => {
              e.preventDefault();
              setOpenAutoComplete((prev) => !prev);
            }}
          >
            <Icon name="user" blue />
          </button>
        ) : null}
      </div>
      <AutoCompleteContacts
        value={mailValue}
        handleClick={handleAddToList}
        open={openAutoComplete}
        list={contactList}
      />
    </label>
  );
};
